import { type Ref } from "@nuxtjs/composition-api";

import { useTranslate } from ".";

interface DebouncedFunction<T extends Array<unknown>> {
  (...args: T): void; // The function can take any arguments
  cancel: () => void; // The cancel method
}

export type VoidFunction<T extends Array<unknown>> = (...args: T) => void;

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @function useDebounce
 * @desc     A simple composable that debounces the function passed to it. takes
 *           in a callback function and a delay number. setTimeout for delay
 *           then calls the callback.
 *
 * @summary  Composable used to debounce a function.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useDebounce = <T extends Array<unknown>>(fn: VoidFunction<T>, delay: number): DebouncedFunction<T> => {
  let timeout: NodeJS.Timeout;

  function executedFunction(...args: T) {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      fn(...args);
    }, delay);
  }

  executedFunction.cancel = function () {
    clearTimeout(timeout);
  };

  return executedFunction;
};

/**
 * Creates a throttled version of a given function that ensures the function
 * is only invoked at most once within the specified time limit.
 *
 * @param func - The function to throttle. It can be any function that takes any arguments.
 * @param limit - The time limit in milliseconds (ms) that must pass before `func` can be called again.
 *               Once the function is invoked, it will not be invoked again until the time limit has passed.
 *
 * @returns A throttled version of the input function. The returned function can be called
 *          as many times as desired, but it will only execute `func` once per `limit` interval.
 *
 * @example
 * const logMessage = (message: string) => console.log(message);
 * const throttledLogMessage = throttle(logMessage, 2000);
 */
export const throttle = <T extends (...args: unknown[]) => void>(
  func: T,
  limit: number,
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;

  return function (...args: Parameters<T>) {
    const now = Date.now();
    if (now - lastCall >= limit) {
      lastCall = now;
      func(...args);
    }
  };
};

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @function useValidation
 * @desc     A simple composable that can be used to validate forms
 *
 * @summary  Composable used to validate a form.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useValidation = () => {
  const { t } = useTranslate();

  const requiredRule = (v: string | number | undefined) =>
    (typeof v === "string" ? !!v?.trim() : !!v) || t("This field is required.").value;

  const emailRule = (v: string) =>
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i.test(
      v,
    ) || t("Please enter a valid email").value;

  const passwordMinRule = (v: string) => {
    const minPasswordLength = 8;
    return (v && v.length >= minPasswordLength) || t("Password must be 8 characters or more.").value;
  };

  const passwordConfirmationRule = (password: string, rePassword: string) =>
    password === rePassword || t("Passwords must match").value;

  const influencerPasswordRule = (v: string) => {
    const pattern = /(?=.{8,})(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*#?&\d])/;
    return (
      pattern.test(v) ||
      t(
        "Password must be 8 characters or more, have at least one lowercase letter, one uppercase letter, one number or special character.",
      ).value
    );
  };

  const validateForm = (formRef: Ref<HTMLFormElement | null>): boolean => {
    return formRef.value?.validate();
  };

  const alphaRule = (v: string) => {
    return /^(0(\.\d+)?|1(\.0+)?)$/.test(v) || t("This field must be a number between 0 and 1").value;
  };

  return {
    requiredRule,
    emailRule,
    passwordMinRule,
    passwordConfirmationRule,
    influencerPasswordRule,
    validateForm,
    alphaRule,
  };
};
